import { ReactNode } from 'react';
import { Breadcrumbs, BreadcrumbItem } from './Breadcrumbs';

/**
 * TopBar — sticky top app bar (Small variant).
 *
 * Derived from M3 Top App Bar (Small variant) per Agent Protocol step 2:
 *   - Spec:      https://m3.material.io/components/top-app-bar/overview
 *   - Reference: github.com/material-components/material-web (no
 *                first-party top-app-bar component at time of authoring;
 *                M3 spec drives the visual)
 *
 * Populus adaptations applied:
 *   - White surface (--color-card) vs M3 dynamic surface (M3 defaults
 *     to primary container; Populus chose white for cleaner business-app
 *     aesthetic + better contrast against the dark Sidebar)
 *   - Breadcrumbs left-aligned in the headline slot (vs M3 page title;
 *     breadcrumbs serve the same orientation function in Populus's
 *     deeper-hierarchy entity-detail flows)
 *   - Right-aligned action cluster (notifications, user menu) — M3
 *     trailing icon convention
 *   - Sticky positioning (M3 implies but doesn't require)
 *   - Height 56px (M3 Small Top App Bar spec)
 *   - Subtle bottom border instead of elevation shadow (vs M3 elevation
 *     level 0-2; cleaner for business-app density)
 *
 * Token discipline:
 *   - Surface → --color-card (system)
 *   - Border → --color-border (system)
 *   - All chrome → --color-* (system; TopBar is operator-tool plumbing,
 *     not a brand surface)
 *
 * Phase 11 Block 6 PR 4 (2026-05-12).
 */

export interface TopBarProps {
  breadcrumbs?: BreadcrumbItem[];
  title?: ReactNode;
  actions?: ReactNode;
}

export function TopBar({ breadcrumbs, title, actions }: TopBarProps) {
  return (
    <header style={{
      gridArea: 'topbar',
      position: 'sticky',
      top: 0,
      zIndex: 5,
      height: 56,
      display: 'flex',
      alignItems: 'center',
      gap: 'var(--sp-4)',
      padding: '0 var(--sp-5)',
      background: 'var(--color-card)',
      borderBottom: '1px solid var(--color-border)',
    }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        {breadcrumbs && breadcrumbs.length > 0 ? (
          <Breadcrumbs items={breadcrumbs} />
        ) : title ? (
          <h1 style={{
            margin: 0,
            fontSize: 'var(--fs-lg)',
            fontWeight: 'var(--fw-semibold)',
            color: 'var(--color-ink)',
            letterSpacing: 'var(--ls-tight)',
          }}>{title}</h1>
        ) : null}
      </div>
      {actions && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
        }}>
          {actions}
        </div>
      )}
    </header>
  );
}
